import { forwardRef, useCallback, useEffect, useState } from "react";
import ReactConfetti from "react-confetti";

/** @typedef {Partial<import("react-confetti/dist/types/Confetti").IConfettiOptions>} IConfettiOptions */
/** @typedef {
 *   IConfettiOptions & Partial<{
 *     targetRef: MutableRefObject<HTMLElement | null>,
 *     verticalOffset: number
 *   }>
 * } FnrConfettiProps */

/** @typedef {import("react").ForwardedRef} ForwardedRef */

// @todo get these param annotations to show up on the prop during usage

/**
 * A generic React Confetti component which performantly responds to resizing,
 * preventing awkward (underlying `Canvas` element) overflows.
 * Defaults to overlaying the `Canvas` across the full `window`, but may also
 * overlay across a specific element (`targetRef`).
 * - `targetRef` - An optional ref to an element to contain confetti
 * - `verticalOffset` - An optional vertical offset for the bounds of `targetRef`
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<FnrConfettiProps> & React.RefAttributes<HTMLCanvasElement>>}
 */
const FnrConfetti = forwardRef(
  /**
   * @param {FnrConfettiProps} props
   * @param {ForwardedRef<HTMLCanvasElement>} forwardedRef
   */
  (props, forwardedRef) => {
    const getCanvasDimensions = useCallback(
      ({ width, top }) => {
        const verticalOffset = props?.verticalOffset;
        return {
          width,
          height:
            window.innerHeight -
            top +
            (typeof verticalOffset === "number" && !Number.isNaN(verticalOffset)
              ? verticalOffset
              : 0),
        };
      },
      [props?.verticalOffset]
    );

    // Borrowed from [React-Use](https://github.com/streamich/react-use)
    // @see https://github.com/streamich/react-use/blob/9ef95352e459dd2920b0492c63c39863024ee852/src/useWindowSize.ts
    const [targetDimensions, setTargetDimensions] = useState(() => {
      const targetEl = props?.targetRef?.current;
      if (!targetEl) {
        return {
          width: window.innerWidth,
          height: window.innerHeight,
        };
      }

      const { top, width } = targetEl.getBoundingClientRect();
      return getCanvasDimensions({
        width,
        top,
      });
    });

    useEffect(
      function updateTargetDimensionsOnResize() {
        let frame = 0;
        const targetEl = props?.targetRef?.current;

        if (!targetEl) {
          const updateWindowDimensions = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
              setTargetDimensions({
                width: window.innerWidth,
                height: window.innerHeight,
              });
            });
          };

          window.addEventListener("resize", updateWindowDimensions);

          return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("resize", updateWindowDimensions);
          };
        }

        const observer = new ResizeObserver((entries) => {
          for (let entry of entries) {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
              setTargetDimensions(
                getCanvasDimensions({
                  width: entry.contentRect.width,
                  top: entry.contentRect.top,
                })
              );
            });
          }
        });

        observer.observe(targetEl);

        return () => {
          cancelAnimationFrame(frame);
          observer.disconnect();
        };
      },
      [getCanvasDimensions, props?.targetRef]
    );

    return (
      <ReactConfetti
        {...props}
        ref={forwardedRef}
        width={targetDimensions.width}
        height={targetDimensions.height}
      />
    );
  }
);

export default FnrConfetti;
